'use client';

/**
 * WebMCP registration (webmachinelearning.github.io/webmcp). Mounted
 * in the (public) layout; renders nothing. Feature-detects
 * `navigator.modelContext` — absent in every browser without the
 * capability, so this is a silent no-op for normal visitors.
 *
 * The API surface is still settling (Chrome's early preview exposes
 * `registerTool(tool)`; the spec draft also describes
 * `provideContext({ tools })`), so both shapes are attempted and every
 * call is wrapped — a vendor shape-change must never break the page.
 */
import { useEffect } from 'react';
import { buildWebmcpTools } from '@/lib/webmcp-tools';

interface ModelContext {
  registerTool?: (tool: unknown, opts?: { signal?: AbortSignal }) => unknown;
  provideContext?: (ctx: { tools: unknown[] }) => unknown;
}

export function WebMcpProvider() {
  useEffect(() => {
    const mc = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
    if (!mc) return;
    const abort = new AbortController();
    const tools = buildWebmcpTools();
    try {
      if (typeof mc.registerTool === 'function') {
        for (const tool of tools) mc.registerTool(tool, { signal: abort.signal });
      } else if (typeof mc.provideContext === 'function') {
        mc.provideContext({ tools });
      }
    } catch {
      // Experimental API — registration failure must never surface.
    }
    return () => abort.abort();
  }, []);
  return null;
}
